/**
 * @file `/account/update` — single-field profile-edit form.
 * @module @labcraft/lab/ui/pages/account/update
 *
 * Same surface for two contexts:
 *
 *   1. Post-approval first visit: the user lands here from the
 *      welcome email (or from any auth callback while
 *      `profileCompletedAt` is null) with `?next=/courses`. They fill
 *      their display name; we POST to `/account/update` and navigate
 *      to the embedded `next`.
 *   2. Returning user: signed-in approved learner clicks "Update
 *      account" in the topbar dropdown. Form pre-fills with current
 *      values; on save we stay on the page with a "Saved" notice.
 *
 * `profile_completed_at` is server-side coalesced — the first
 * successful submit stamps it; subsequent submits leave it alone, so
 * a user editing their name later doesn't reset the gate.
 */

import { useState, type FormEvent, type JSX } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { track } from '@labcraft/ui/hooks'
import { useMe } from '../../hooks/use_me.ts'
import { useUpdateProfileMutation } from '../../hooks/use_update_profile.ts'
import styles from '../auth/auth.module.css'

export function AccountUpdatePage(): JSX.Element {
  const me = useMe()
  const updateProfile = useUpdateProfileMutation()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const next = search.get('next')

  const learner = me.data?.learner ?? null
  const [displayName, setDisplayName] = useState(learner?.displayName ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const trimmed = displayName.trim()
  const submitting = updateProfile.isPending
  const canSubmit =
    !submitting && trimmed.length > 0 && trimmed.length <= 80 && learner != null

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!canSubmit) return
    setError(null)
    setSaved(false)
    // Onboarding-vs-edit is decided *before* the submit stamps
    // `profileCompletedAt`: a null value here means this is the
    // first-visit onboarding completion, which is the acquisition
    // funnel's `auth_profile_complete` beat. A returning learner
    // editing their name later already has the timestamp, so their
    // save is a plain edit and fires nothing.
    const isOnboarding = learner?.profileCompletedAt == null
    try {
      await updateProfile.mutateAsync({ displayName: trimmed })
      if (isOnboarding) track('auth_profile_complete')
      if (next) {
        navigate(next, { replace: true })
        return
      }
      setSaved(true)
    } catch {
      setError(
        'Could not save your changes. Refresh the page and try again — if it persists, sign out and back in.',
      )
    }
  }

  return (
    <main className={styles.main}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>Your account</p>
        <h1 className={styles.title}>
          {learner?.profileCompletedAt
            ? 'Update your account'
            : 'One quick thing before you start.'}
        </h1>
        <p className={styles.lede}>
          {learner?.profileCompletedAt
            ? 'Change how your name shows up in the chapter chrome and on work you share.'
            : 'Pick a display name — it’s how we’ll refer to you in the chapter chrome and on anything you share. Nothing permanent; change it whenever you like.'}
        </p>

        {error && <div className={styles.errorBanner}>{error}</div>}
        {saved && (
          <div className={styles.noticeBanner} role="status">
            Saved.
          </div>
        )}

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <label className={styles.field}>
            <span className={styles.label}>Display name</span>
            <input
              className={styles.input}
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              autoComplete="name"
              maxLength={80}
              required
            />
          </label>
          <button className={styles.cta} type="submit" disabled={!canSubmit}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </form>

        <p className={styles.fineprint}>
          Signed in as {learner?.email ?? '(loading)'}.
        </p>
      </section>
    </main>
  )
}
